import { getMemaraApiErrorCode } from './errorUtils';
import { errorTracker } from './errorTracking';

const SAMPLING_RATE = import.meta.env.VITE_ANALYTICS_SAMPLING_RATE
  ? parseFloat(String(import.meta.env.VITE_ANALYTICS_SAMPLING_RATE))
  : 0.1;

const MESSAGE_MAX_LEN = 200;

const apiFailureThrottleMs = 60_000;
const apiThrottleLast = new Map<string, number>();

/** Visible when debugging or when explicitly widening client API error capture. */
export function isVerboseApiAnalytics(): boolean {
  return (
    import.meta.env.DEV === true ||
    import.meta.env.VITE_ANALYTICS_DEBUG === 'true' ||
    import.meta.env.VITE_ANALYTICS_VERBOSE_API_ERRORS === 'true'
  );
}

function randomSamplePass(): boolean {
  return Math.random() < SAMPLING_RATE;
}

/** Remove email-like substrings from analytics payloads (PII). */
export function redactEmailLike(text: string): string {
  return text.replace(/\S+@\S+\.\S+/g, '[redacted_email]');
}

export function truncateForAnalytics(text: string, maxLen: number = MESSAGE_MAX_LEN): string {
  const t = redactEmailLike(text).trim();
  if (t.length <= maxLen) {
    return t;
  }
  return `${t.slice(0, maxLen)}…`;
}

export type HandledOperationSeverity = 'low' | 'medium' | 'high';

export interface HandledOperationFailureProps {
  surface: string;
  operation: string;
  severity?: HandledOperationSeverity;
  provider_error_code?: string;
  provider_status?: number;
  http_status?: number;
  memara_error_code?: string;
  request_id?: string | null;
  api_path?: string;
  /** Short, redacted diagnostic (no raw PII). */
  message_hint?: string;
}

/**
 * Selective rules for Memara API failures from the axios client (quota-conscious).
 * Always: 5xx, 401, 403, 429. Routine 404/422: sampled unless verbose mode.
 */
export function shouldReportApiClientFailure(status: number | undefined): {
  report: boolean;
  useSampling: boolean;
} {
  if (status == null || Number.isNaN(status)) {
    return { report: true, useSampling: false };
  }
  if (status >= 500) {
    return { report: true, useSampling: false };
  }
  if (status === 401 || status === 403 || status === 429) {
    return { report: true, useSampling: false };
  }
  if (status === 404 || status === 422) {
    if (isVerboseApiAnalytics()) {
      return { report: true, useSampling: false };
    }
    return { report: true, useSampling: true };
  }
  return { report: false, useSampling: false };
}

function throttleAllow(key: string): boolean {
  const now = Date.now();
  const last = apiThrottleLast.get(key);
  if (last == null || now - last > apiFailureThrottleMs) {
    apiThrottleLast.set(key, now);
    return true;
  }
  return false;
}

function normalizeApiPath(url: string | undefined): string | undefined {
  if (!url || typeof url !== 'string') {
    return undefined;
  }
  try {
    if (url.startsWith('http')) {
      const u = new URL(url);
      return u.pathname;
    }
    return url.split('?')[0];
  } catch {
    return url.split('?')[0];
  }
}

/**
 * PostHog custom event for handled failures (auth, API, etc.). Does not use $exception.
 */
export function captureHandledOperationFailure(
  props: HandledOperationFailureProps,
  posthogInstance?: { capture: (event: string, properties?: Record<string, unknown>) => void } | null
): void {
  const posthog = posthogInstance ?? errorTracker.getPostHog();
  if (!posthog) {
    return;
  }
  try {
    const payload: Record<string, unknown> = {
      surface: props.surface,
      operation: props.operation,
      severity: props.severity ?? 'medium',
      timestamp: new Date().toISOString(),
      current_url:
        typeof window !== 'undefined' ? window.location.pathname : undefined,
    };
    if (props.provider_error_code != null) {
      payload.provider_error_code = props.provider_error_code;
    }
    if (props.provider_status != null) {
      payload.provider_status = props.provider_status;
    }
    if (props.http_status != null) {
      payload.http_status = props.http_status;
    }
    if (props.memara_error_code != null) {
      payload.memara_error_code = props.memara_error_code;
    }
    if (props.request_id != null && props.request_id !== '') {
      payload.request_id = props.request_id;
    }
    if (props.api_path != null) {
      payload.api_path = props.api_path;
    }
    if (props.message_hint != null && props.message_hint !== '') {
      payload.message_hint = truncateForAnalytics(props.message_hint);
    }
    posthog.capture('client_handled_operation_failed', payload);
  } catch {
    // Never break callers
  }
}

/** Axios-shaped error from response interceptor. */
/** Map Supabase Auth errors to PostHog (no email or tokens). */
export function captureSupabaseAuthFailure(
  posthog: { capture: (event: string, properties?: Record<string, unknown>) => void } | null | undefined,
  args: {
    surface: string;
    operation: string;
    error: unknown;
    severity?: HandledOperationSeverity;
  }
): void {
  const err = args.error as {
    name?: string;
    message?: string;
    status?: number;
    code?: string;
  };
  const code =
    typeof err?.name === 'string'
      ? err.name
      : typeof err?.code === 'string'
        ? err.code
        : undefined;
  const hint =
    typeof err?.message === 'string'
      ? err.message
      : typeof args.error === 'string'
        ? args.error
        : args.error != null
          ? String(args.error)
          : undefined;
  captureHandledOperationFailure(
    {
      surface: args.surface,
      operation: args.operation,
      severity: args.severity ?? 'high',
      provider_error_code: code,
      provider_status: typeof err?.status === 'number' ? err.status : undefined,
      message_hint: hint,
    },
    posthog ?? undefined
  );
}

export function reportApiClientFailureFromAxios(
  error: unknown,
  posthogInstance?: { capture: (event: string, properties?: Record<string, unknown>) => void } | null,
  headerOrFallbackRequestId?: string | null
): void {
  const e = error as {
    response?: { status?: number; data?: unknown };
    config?: { method?: string; url?: string };
  };
  const status = e.response?.status;
  const { report, useSampling } = shouldReportApiClientFailure(status);
  if (!report) {
    return;
  }
  if (useSampling && !randomSamplePass()) {
    return;
  }

  const method = (e.config?.method ?? 'GET').toUpperCase();
  const apiPath = normalizeApiPath(e.config?.url);
  const memaraCode = getMemaraApiErrorCode(error);
  const throttleKey = `${method}:${apiPath ?? 'unknown'}:${status ?? 'none'}:${memaraCode ?? 'none'}`;
  if (!throttleAllow(throttleKey)) {
    return;
  }

  const ridFromBody =
    typeof e.response?.data === 'object' &&
    e.response?.data !== null &&
    'request_id' in e.response.data &&
    typeof (e.response.data as { request_id?: unknown }).request_id === 'string'
      ? (e.response.data as { request_id: string }).request_id
      : undefined;
  const rid =
    ridFromBody ??
    (headerOrFallbackRequestId && headerOrFallbackRequestId.length > 0
      ? headerOrFallbackRequestId
      : undefined);

  captureHandledOperationFailure(
    {
      surface: 'memara_api_client',
      operation: 'axios_response_error',
      severity: status != null && status >= 500 ? 'high' : 'medium',
      http_status: status,
      memara_error_code: memaraCode,
      request_id: rid,
      api_path: apiPath,
    },
    posthogInstance
  );
}
